import React from "react";
import { Col, Row, Form } from "antd";
import _ from "lodash";

import formFieldComponents from "../components/form/utils/formFieldComponents";
import CustomFieldEngine from "../../../customField/customFieldEngine";
import SectionPart from "../components/SectionPart";
import * as validationRules from "./index";
import { evaluateFieldCondition } from "./formConditions";
import { FORM_FIELD_TYPES } from "../constants";

// A checkbox that neither maps to the BE (no BEName) nor declares a default is a pure UI toggle (e.g.
// the insurance "card has no BIN/PCN" switches). It must NOT seed a `false` initial, so an untouched one
// stays out of the submitted bundle. A mapped or defaulted checkbox keeps its `false` seed so the BE
// still receives it — the hand-written forms did (MVP-31021, Jehad QA Findings C / G).
const isUnmappedUiCheckbox = (field) =>
  field.type === FORM_FIELD_TYPES.CHECKBOX &&
  !field.BEName &&
  field.defaultValue === undefined &&
  field.dedicatedFieldProps?.defaultValue === undefined &&
  !field.conditions?.defaultValue;

export const getFieldName = (sectionName, field) => {
  if (field.subSectionName) {
    return [sectionName, field.subSectionName, field.name];
  }
  return [sectionName, field.name];
};

export const getValidationRules = (field) => {
  const { rules: ruleNames = [], rulesParams = {} } = field;
  if (!ruleNames.length) {
    return [];
  }
  const rulesArr = ruleNames.map((ruleName) => {
    const rule = validationRules[ruleName];
    if (!rule) {
      return null;
    }
    if (typeof rule === "function" && rulesParams[ruleName]) {
      return rule(rulesParams[ruleName] || {});
    }
    return rule;
  });

  return _.compact(_.flatten(rulesArr));
};

// A section skips the SectionPart divider/anchor when one of its fields renders its OWN section chrome
// (divider + anchor id) — e.g. the native `eligibilityQuestionnaire` field via DynamicQuestionnaire — so
// the wrapper doesn't add an empty divider or a duplicate `enrollment-form-*` id. Deriving this from the
// field type (the component declares `rendersOwnSection`) keeps the decision where the knowledge lives
// and removes the need for a per-section flag + `sectionHeader: ""` sentinel. `layout: "bare"` is an
// explicit opt-out for a deliberately wrapper-less section of otherwise-normal fields.
const sectionRendersOwnChrome = (section) =>
  section?.layout === "bare" ||
  (section?.rows || []).some((row) =>
    (row.fields || []).some((field) => formFieldComponents[field.type]?.rendersOwnSection)
  );

// Evaluate a field's visibility / required / disabled / hidden against the current form values.
const computeFieldState = (field, form) => {
  let isVisible = true;
  let isDisabled = field.fieldProps?.disabled || false;
  let isRequired = field.required || false;
  // Seed from a static formItemProps.hidden (e.g. the patient `id` field) so it collapses too.
  let isHidden = field.formItemProps?.hidden || false;

  if (field.conditions) {
    const { visibility, hidden, disabled, required } = field.conditions;
    if (visibility) {
      isVisible = evaluateFieldCondition(visibility, form);
    }
    if (required) {
      isRequired = evaluateFieldCondition(required, form);
    }
    if (disabled) {
      isDisabled = evaluateFieldCondition(disabled, form);
    }
    // `hidden` differs from `visibility`: the field stays MOUNTED (registered in getFieldsValue and
    // therefore submitted) but is visually hidden — mirrors the old `<FormItem hidden>`.
    if (hidden) {
      isHidden = evaluateFieldCondition(hidden, form);
    }
  }

  return { isVisible, isDisabled, isRequired, isHidden };
};

const renderFieldColumn = ({ section, field, form, isVisible, isDisabled, isRequired, isHidden }) => {
  if (!isVisible) {
    return null;
  }
  const FieldComponent = formFieldComponents[field.type] || CustomFieldEngine;
  const span = field.colSize || 8;
  // A hidden field stays mounted (registered/submitted) but collapses to span 0 so it occupies no grid
  // space and its siblings wrap identically to the old `<Col span={x !== OTHER ? 0 : 8}>`.
  return (
    <Col span={isHidden ? 0 : span} key={`${section.sectionName}_${field.name}_${field.label}`}>
      <FieldComponent
        type={field.type}
        name={getFieldName(section.sectionName, field)}
        label={field.label}
        required={isRequired}
        rules={getValidationRules(field)}
        placeholder={field.placeholder}
        fieldProps={{ ...field.fieldProps, ...(isDisabled && { disabled: true }) }}
        formItemProps={isHidden ? { ...field.formItemProps, hidden: true } : field.formItemProps}
        form={form}
        alternativeFields={field.alternativeFields}
        {...(field.type === FORM_FIELD_TYPES.CHECKBOX && { seedInitialValue: !isUnmappedUiCheckbox(field) })}
        {...field.dedicatedFieldProps}
      />
    </Col>
  );
};

// The `relatedFieldName`s a field's conditions read — the fields whose changes must re-evaluate it.
const getConditionDependencies = (conditions) => {
  const deps = new Set();
  const collect = (rules) => (rules || []).forEach((rule) => rule?.relatedFieldName && deps.add(rule.relatedFieldName));
  Object.values(conditions || {}).forEach((condition) => {
    collect(condition?.rules);
    if (condition?.multiLevel) {
      collect(condition.multiLevel.outerRules);
      collect(condition.multiLevel.innerRules);
    }
  });
  return [...deps];
};

// Watches one dependency field and notifies the parent when it changes. One instance per dependency so
// each can call the single-NamePath Form.useWatch (the selector form does not re-render on
// setFieldsValue in antd 4, and hooks can't run in a loop).
const DependencyWatcher = ({ path, form, onChange }) => {
  const value = Form.useWatch(path, form);
  React.useEffect(() => {
    onChange();
  }, [value, onChange]);
  return null;
};

// A field whose visibility / required / disabled depends on OTHER fields must re-evaluate whenever those
// change — INCLUDING on programmatic form.setFieldsValue (e.g. the physician / facility "Other" button),
// which antd does not surface through the form's onFieldsChange, so the parent tree would not otherwise
// re-render. This subscribes to the dependency fields and, once the field turns required + visible,
// validates it so its required error shows immediately like the always-required fields (which validate
// from mount). Without this, a conditionally-required field that appears via such a change stayed
// un-validated and looked like its validation was missing (MVP-31021 / MVP-31223 QA — the physician /
// prescriber first & last names on the "Other" path).
const ConditionalFieldRenderer = ({ section, field, form }) => {
  const dependencies = React.useMemo(() => getConditionDependencies(field.conditions), [field]);
  const [, forceReevaluate] = React.useState(0);
  const onDependencyChange = React.useCallback(() => forceReevaluate((tick) => tick + 1), []);

  const state = computeFieldState(field, form);
  const fieldName = React.useMemo(() => getFieldName(section.sectionName, field), [section.sectionName, field]);

  React.useEffect(() => {
    if (state.isRequired && !state.isHidden && state.isVisible) {
      form.validateFields([fieldName]).catch(() => {});
    }
  }, [state.isRequired, state.isHidden, state.isVisible, form, fieldName]);

  return (
    <>
      {dependencies.map((dependency) => (
        <DependencyWatcher key={dependency} path={dependency.split(".")} form={form} onChange={onDependencyChange} />
      ))}
      {renderFieldColumn({ section, field, form, ...state })}
    </>
  );
};

const renderReactFromJson = (form, json) => {
  return json.formSections.map((section) => {
    if (section.conditions?.visibility && !evaluateFieldCondition(section.conditions.visibility, form)) {
      return null;
    }
    const rows = renderReactRowsJson({ section, rows: section.rows, form });
    if (sectionRendersOwnChrome(section)) {
      return <React.Fragment key={section.sectionName}>{rows}</React.Fragment>;
    }
    return (
      <SectionPart
        key={section.sectionName}
        sectionId={section.sectionName}
        headerTranslationKey={section.sectionHeader}
      >
        {rows}
      </SectionPart>
    );
  });
};

export const renderReactRowsJson = ({ section, rows, form }) => {
  return rows.map((row, rowIndex) => (
    <Row key={`${section?.sectionName}_row_${rowIndex}`} gutter={[16, 16]}>
      {row.fields.map((field) => {
        // Fields whose conditions depend on other fields need to re-evaluate reactively (see
        // ConditionalFieldRenderer). Fields without conditions render on the parent's normal render pass.
        if (field.conditions) {
          return (
            <ConditionalFieldRenderer
              key={`${section.sectionName}_${field.name}_${field.label}`}
              section={section}
              field={field}
              form={form}
            />
          );
        }
        return renderFieldColumn({ section, field, form, ...computeFieldState(field, form) });
      })}
    </Row>
  ));
};

export default renderReactFromJson;
